import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Star, Sparkles } from 'lucide-react';

const PALETTE = [
  '#b89dd4', // lavanda principal
  '#7a5aa2', // morado profundo
  '#cdb2e0', // lila claro
  '#c28fb4', // rosa morado
  '#e9c1d5', // rosa pálido
  '#e6b76b', // dorado
  '#f1c77a', // amarillo cálido
  '#e8d36a', // amarillo tulipán
  '#9ab86a', // verde suave
  '#a27a57'  // marrón cálido
];

const rand = (min, max) => Math.random() * (max - min) + min;

// How long the rain keeps emitting per click (ms) — short burst of emission
const RAIN_DURATION_MS = 1600;
// How often new drops are added (ms between emits)
const EMIT_INTERVAL_MS = 60;
// Drops emitted per tick
const DROPS_PER_TICK = 7;
// How long each drop falls — slow & natural
const DROP_LIFE_MIN = 5400;
const DROP_LIFE_MAX = 7500;

function makeParticle(originX, originY) {
  const kind = Math.random() < 0.18 ? 'sparkle' : Math.random() < 0.55 ? 'star' : 'dot';
  const size = kind === 'dot' ? rand(3, 8) : rand(9, 20);

  // Detect viewport: on desktop/web we use a much wider spread so the star
  // dust reaches well past the tabs on both sides. Mobile stays compact.
  const vw = typeof window !== 'undefined'
    ? (window.innerWidth || document.documentElement.clientWidth || 1024)
    : 1024;
  const isDesktop = vw >= 900;
  const maxSpreadVW = isDesktop ? 85 : 119; // web: ±85vw, mobile: ±119vw (prior 99 +20%)

  const side = Math.random() < 0.5 ? -1 : 1;
  const spread = Math.random(); // uniform — fills center as much as edges
  const arcX = side * spread * maxSpreadVW;
  const fallVH = rand(95, 130);

  return {
    id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    color: PALETTE[Math.floor(Math.random() * PALETTE.length)],
    arcX,
    fallVH,
    rotate: rand(-140, 140),
    durationMs: rand(DROP_LIFE_MIN, DROP_LIFE_MAX),
    size,
    kind,
    originX: originX + rand(-4, 4),
    originY: originY + rand(-3, 3)
  };
}

function Particle({ particle }) {
  const { color, size, kind, rotate, arcX, fallVH, durationMs, originX, originY } = particle;

  const style = {
    left: `${originX}px`,
    top: `${originY}px`,
    color,
    ['--arcX']: `${arcX}vw`,
    ['--fallY']: `${fallVH}vh`,
    ['--rot']: `${rotate}deg`,
    animation: `dust-rain ${durationMs}ms linear forwards`
  };

  if (kind === 'star') {
    return <Star style={style} width={size} height={size} className="dust-particle" strokeWidth={1.4} fill={color} />;
  }
  if (kind === 'sparkle') {
    return <Sparkles style={style} width={size} height={size} className="dust-particle" strokeWidth={1.5} />;
  }
  return (
    <span
      style={{ ...style, backgroundColor: color, width: size, height: size }}
      className="dust-particle dust-dot"
    />
  );
}

/**
 * MagicDust wraps the logo. On click, starts a CONTINUOUS rain of colored
 * stars/dots falling from the logo origin for ~9 seconds, plus a slow-falling
 * phrase "Polvito mágico para ti". Particles are rendered through a React
 * portal so they float above all page content (the logo itself stays visible).
 */
export default function MagicDust({ children, width = 60 }) {
  const anchorRef = useRef(null);
  const [particles, setParticles] = useState([]);
  const [phrases, setPhrases] = useState([]);
  const [mounted, setMounted] = useState(false);

  // Refs for active emitters, so we can stop them on unmount
  const emitIntervalRef = useRef(null);
  const emitStopTimeoutRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    return () => {
      if (emitIntervalRef.current) clearInterval(emitIntervalRef.current);
      if (emitStopTimeoutRef.current) clearTimeout(emitStopTimeoutRef.current);
    };
  }, []);

  const trigger = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;

    // Drop a new phrase for this click
    const phraseId = `ph-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`;
    setPhrases((prev) => [...prev, { id: phraseId, originX, originY }]);
    setTimeout(() => {
      setPhrases((prev) => prev.filter((p) => p.id !== phraseId));
    }, 4800);

    // Start continuous rain emission
    // Clear any previous active emitter so repeated clicks refresh the rain
    if (emitIntervalRef.current) clearInterval(emitIntervalRef.current);
    if (emitStopTimeoutRef.current) clearTimeout(emitStopTimeoutRef.current);

    const emitStart = Date.now();
    emitIntervalRef.current = setInterval(() => {
      const nowRect = anchorRef.current && anchorRef.current.getBoundingClientRect();
      const ox = nowRect ? nowRect.left + nowRect.width / 2 : originX;
      const oy = nowRect ? nowRect.top + nowRect.height / 2 : originY;

      const fresh = Array.from({ length: DROPS_PER_TICK }).map(() => makeParticle(ox, oy));
      setParticles((prev) => [...prev, ...fresh]);

      // Each particle self-cleans after its lifetime
      fresh.forEach((p) => {
        setTimeout(() => {
          setParticles((prev) => prev.filter((x) => x.id !== p.id));
        }, p.durationMs + 150);
      });

      if (Date.now() - emitStart >= RAIN_DURATION_MS) {
        clearInterval(emitIntervalRef.current);
        emitIntervalRef.current = null;
      }
    }, EMIT_INTERVAL_MS);

    // Safety stop in case interval never clears
    emitStopTimeoutRef.current = setTimeout(() => {
      if (emitIntervalRef.current) {
        clearInterval(emitIntervalRef.current);
        emitIntervalRef.current = null;
      }
    }, RAIN_DURATION_MS + 200);
  }, []);

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      trigger();
    }
  };

  const portal = mounted && typeof document !== 'undefined'
    ? createPortal(
        <div className="magic-dust-portal">
          {particles.map((p) => (
            <Particle key={p.id} particle={p} />
          ))}
          {phrases.map((ph) => (
            <span
              key={ph.id}
              className="magic-dust-phrase"
              style={{ left: `${ph.originX}px`, top: `${ph.originY}px` }}
            >
              Polvito mágico para ti
            </span>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div
        ref={anchorRef}
        role="button"
        tabIndex={0}
        aria-label="Polvito mágico"
        onClick={trigger}
        onKeyDown={handleKey}
        className="magic-dust-wrap"
        style={{ width, height: width }}
      >
        {children}
      </div>
      {portal}
    </>
  );
}
